import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './OllamaChat.css';

const OllamaChat = () => {
  const { token } = useAuth();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchWeb, setSearchWeb] = useState(false);
  const [lastWebSearch, setLastWebSearch] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const headers = useCallback(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const res = await fetch('/api/ollama/models', { headers: headers() });
        const data = await res.json();
        if (data.success && data.models.length > 0) {
          setModels(data.models);
          setSelectedModel(data.models[0]);
        }
      } catch (e) {
        setError('Impossible de contacter Ollama: ' + e.message);
      }
    };
    fetchModels();
  }, [headers]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setError('');
    setLoading(true);
    setLastWebSearch(false);

    try {
      const res = await fetch('/api/ollama/chat', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ model: selectedModel, messages: newMessages, search_web: searchWeb }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Erreur inconnue');
      setLastWebSearch(data.web_search_used === true);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message,
        webSearch: data.web_search_used === true,
      }]);
    } catch (e) {
      setError(e.message);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearConversation = () => {
    setMessages([]);
    setError('');
    setLastWebSearch(false);
  };

  return (
    <div className="ollama-page">
      <div className="ollama-header">
        <div>
          <h1 className="ollama-title">Chat IA</h1>
          <p className="ollama-subtitle">Conversation avec l'IA locale (Ollama)</p>
        </div>
        <div className="ollama-controls">
          <select
            className="ollama-model-select"
            value={selectedModel}
            onChange={e => setSelectedModel(e.target.value)}
            disabled={loading}
          >
            {models.length === 0 && <option value="">Chargement...</option>}
            {models.map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          <label className={`ollama-web-toggle ${searchWeb ? 'active' : ''}`} title="Forcer la recherche web pour toutes les questions (sinon détection automatique)">
            <input
              type="checkbox"
              checked={searchWeb}
              onChange={e => setSearchWeb(e.target.checked)}
              disabled={loading}
            />
            🌐 Forcer recherche web
          </label>

          {messages.length > 0 && (
            <button className="ollama-clear-btn" onClick={clearConversation} disabled={loading}>
              Nouvelle conversation
            </button>
          )}
        </div>
      </div>

      {error && <div className="ollama-error">{error}</div>}

      <div className="ollama-messages">
        {messages.length === 0 && !loading && (
          <div className="ollama-empty">
            <div className="ollama-empty-icon">🤖</div>
            <p>Posez votre question à l'IA</p>
            <p className="ollama-empty-hint">Entrée pour envoyer · Shift+Entrée pour saut de ligne</p>
            <p className="ollama-empty-hint ollama-web-hint">🌐 Recherche web automatique pour les questions en temps réel (météo, cours, actualités…)</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`ollama-message ollama-message--${msg.role}`}>
            <div className="ollama-message-avatar">
              {msg.role === 'user' ? '👤' : '🤖'}
            </div>
            <div className="ollama-message-bubble">
              {msg.webSearch && (
                <div className="ollama-web-badge">🌐 Réponse enrichie par une recherche web en temps réel</div>
              )}
              <pre className="ollama-message-content">{msg.content}</pre>
            </div>
          </div>
        ))}

        {loading && (
          <div className="ollama-message ollama-message--assistant">
            <div className="ollama-message-avatar">🤖</div>
            <div className="ollama-message-bubble ollama-message-bubble--typing">
              {searchWeb && <span className="ollama-searching">🌐 Recherche en cours...</span>}
              <span className="ollama-dot" />
              <span className="ollama-dot" />
              <span className="ollama-dot" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="ollama-input-area">
        <textarea
          ref={textareaRef}
          className="ollama-input"
          placeholder="Écrivez votre message..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading || !selectedModel}
          rows={3}
        />
        <button
          className="ollama-send-btn"
          onClick={sendMessage}
          disabled={loading || !input.trim() || !selectedModel}
        >
          {loading ? '⏳' : '➤'}
        </button>
      </div>
    </div>
  );
};

export default OllamaChat;
